'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  initializeData,
  getBrands,
  getSeries,
  getModels,
  getErrorCategories,
  Brand,
  Series,
  Model,
  ErrorCategory,
} from '@/lib/dataStore';
import { Mail, ChevronDown, CheckCircle, ArrowLeft, Gamepad2, Send } from 'lucide-react';

interface FormData {
  brandId: string;
  seriesId: string;
  modelId: string;
  errorCategoryId: string;
  customRepairType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

function RepairRequestForm() {
  const searchParams = useSearchParams();
  const prefilledModelId = searchParams.get('modelId') ?? '';
  const prefilledErrorCategoryId = searchParams.get('errorCategoryId') ?? '';

  const [brands, setBrands] = useState<Brand[]>([]);
  const [allSeries, setAllSeries] = useState<Series[]>([]);
  const [allModels, setAllModels] = useState<Model[]>([]);
  const [allErrorCategories, setAllErrorCategories] = useState<ErrorCategory[]>([]);

  const [form, setForm] = useState<FormData>({
    brandId: '',
    seriesId: '',
    modelId: '',
    errorCategoryId: '',
    customRepairType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    initializeData();
    const loadedBrands = getBrands();
    const loadedSeries = getSeries();
    const loadedModels = getModels();
    const loadedErrors = getErrorCategories();

    setBrands(loadedBrands);
    setAllSeries(loadedSeries);
    setAllModels(loadedModels);
    setAllErrorCategories(loadedErrors);

    // Pre-fill from URL params
    if (prefilledModelId) {
      const model = loadedModels.find(m => m.id === prefilledModelId);
      if (model) {
        const series = loadedSeries.find(s => s.id === model.seriesId);
        const brandId = series?.brandId ?? '';
        setForm(prev => ({
          ...prev,
          brandId,
          seriesId: model.seriesId,
          modelId: prefilledModelId,
          errorCategoryId: prefilledErrorCategoryId,
        }));
      }
    }
  }, [prefilledModelId, prefilledErrorCategoryId]);

  const filteredSeries = allSeries.filter(s => s.brandId === form.brandId);
  const filteredModels = allModels.filter(m => m.seriesId === form.seriesId);
  const filteredErrors = allErrorCategories.filter(e => e.modelId === form.modelId);

  const selectedBrand = brands.find(b => b.id === form.brandId);
  const selectedSeries = allSeries.find(s => s.id === form.seriesId);
  const selectedModel = allModels.find(m => m.id === form.modelId);
  const selectedError = allErrorCategories.find(e => e.id === form.errorCategoryId);

  function handleChange(field: keyof FormData, value: string) {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'brandId') {
        updated.seriesId = '';
        updated.modelId = '';
        updated.errorCategoryId = '';
      } else if (field === 'seriesId') {
        updated.modelId = '';
        updated.errorCategoryId = '';
      } else if (field === 'modelId') {
        updated.errorCategoryId = '';
      }
      return updated;
    });
    setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.brandId) newErrors.brandId = 'Bitte Marke wählen';
    if (!form.seriesId) newErrors.seriesId = 'Bitte Serie wählen';
    if (!form.modelId) newErrors.modelId = 'Bitte Modell wählen';
    if (!form.errorCategoryId && !form.customRepairType.trim())
      newErrors.errorCategoryId = 'Bitte Reparaturart wählen oder beschreiben';
    if (!form.firstName.trim()) newErrors.firstName = 'Vorname erforderlich';
    if (!form.lastName.trim()) newErrors.lastName = 'Nachname erforderlich';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = 'Gültige E-Mail-Adresse erforderlich';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function buildMailtoLink(): string {
    const brand = selectedBrand?.name ?? form.brandId;
    const series = selectedSeries?.name ?? form.seriesId;
    const model = selectedModel?.name ?? form.modelId;
    const repair = selectedError?.name ?? form.customRepairType;

    const subject = encodeURIComponent(`Reparaturanfrage: ${brand} ${model} – ${repair}`);
    const body = encodeURIComponent(
      `Neue Reparaturanfrage\n` +
      `========================\n\n` +
      `Konsole:\n` +
      `  Marke: ${brand}\n` +
      `  Serie: ${series}\n` +
      `  Modell: ${model}\n` +
      `  Reparaturart: ${repair}\n\n` +
      `Kontakt:\n` +
      `  Name: ${form.firstName} ${form.lastName}\n` +
      `  E-Mail: ${form.email}\n` +
      `  Telefon: ${form.phone || '–'}\n\n` +
      `Nachricht:\n` +
      `  ${form.message || '–'}\n`
    );
    return `mailto:info@mediarabbit.de?subject=${subject}&body=${body}`;
  }

  function buildSuccessUrl(): string {
    const brand = selectedBrand?.name ?? form.brandId;
    const series = selectedSeries?.name ?? form.seriesId;
    const model = selectedModel?.name ?? form.modelId;
    const repair = selectedError?.name ?? form.customRepairType;
    const params = new URLSearchParams();
    if (brand) params.set('brand', brand);
    if (series) params.set('series', series);
    if (model) params.set('model', model);
    if (repair) params.set('repair', repair);
    if (form.firstName || form.lastName) params.set('name', `${form.firstName} ${form.lastName}`.trim());
    if (form.email) params.set('email', form.email);
    return `/reparatur-erfolg?${params.toString()}`;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    window.location.href = buildMailtoLink();
    setTimeout(() => {
      window.location.href = buildSuccessUrl();
    }, 500);
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">Anfrage wird gesendet</h1>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Ihr E-Mail-Programm öffnet sich mit Ihrer vorausgefüllten Anfrage.
              Bitte senden Sie die E-Mail ab, um Ihre Anfrage abzuschließen.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/" className="btn-primary inline-flex items-center gap-2 justify-center">
                <ArrowLeft size={16} />
                Zurück zur Startseite
              </Link>
              <button
                onClick={() => setSubmitted(false)}
                className="btn-ghost inline-flex items-center gap-2 justify-center"
              >
                Neue Anfrage
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-screen-lg mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <Link href="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors mb-4">
            <ArrowLeft size={16} />
            Zurück zur Startseite
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">Reparatur anfragen</h1>
          <p className="text-muted-foreground">Füllen Sie das Formular aus – wir melden uns schnellstmöglich bei Ihnen.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-8">
            {/* Step 1: Console Selection */}
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold text-sm">1</div>
                <div>
                  <h2 className="font-semibold text-foreground">Konsole auswählen</h2>
                  <p className="text-xs text-muted-foreground">Marke, Serie und Modell Ihrer Konsole</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Marke *</label>
                  <div className="relative">
                    <select
                      value={form.brandId}
                      onChange={e => handleChange('brandId', e.target.value)}
                      className={`input-field appearance-none pr-8 ${errors.brandId ? 'border-red-400' : ''}`}
                    >
                      <option value="">Marke wählen</option>
                      {brands.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                  {errors.brandId && <p className="text-red-500 text-xs mt-1">{errors.brandId}</p>}
                </div>

                {/* Series */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Serie *</label>
                  <div className="relative">
                    <select
                      value={form.seriesId}
                      onChange={e => handleChange('seriesId', e.target.value)}
                      disabled={!form.brandId}
                      className={`input-field appearance-none pr-8 disabled:opacity-50 ${errors.seriesId ? 'border-red-400' : ''}`}
                    >
                      <option value="">Serie wählen</option>
                      {filteredSeries.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                  {errors.seriesId && <p className="text-red-500 text-xs mt-1">{errors.seriesId}</p>}
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Modell *</label>
                  <div className="relative">
                    <select
                      value={form.modelId}
                      onChange={e => handleChange('modelId', e.target.value)}
                      disabled={!form.seriesId}
                      className={`input-field appearance-none pr-8 disabled:opacity-50 ${errors.modelId ? 'border-red-400' : ''}`}
                    >
                      <option value="">Modell wählen</option>
                      {filteredModels.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                  {errors.modelId && <p className="text-red-500 text-xs mt-1">{errors.modelId}</p>}
                </div>
              </div>
            </div>

            {/* Step 2: Repair Type */}
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold text-sm">2</div>
                <div>
                  <h2 className="font-semibold text-foreground">Reparaturart</h2>
                  <p className="text-xs text-muted-foreground">Was soll repariert werden?</p>
                </div>
              </div>

              {form.modelId && filteredErrors.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Reparaturart wählen *</label>
                  <div className="relative">
                    <select
                      value={form.errorCategoryId}
                      onChange={e => handleChange('errorCategoryId', e.target.value)}
                      className={`input-field appearance-none pr-8 ${errors.errorCategoryId ? 'border-red-400' : ''}`}
                    >
                      <option value="">Reparaturart wählen</option>
                      {filteredErrors.map(e => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                  {errors.errorCategoryId && <p className="text-red-500 text-xs mt-1">{errors.errorCategoryId}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    {form.modelId ? 'Reparatur beschreiben *' : 'Reparatur beschreiben (oder zuerst Modell wählen)'}
                  </label>
                  <textarea
                    value={form.customRepairType}
                    onChange={e => handleChange('customRepairType', e.target.value)}
                    placeholder="z.B. Display-Reparatur, Akku-Austausch, Joy-Con-Drift..."
                    rows={3}
                    className={`input-field resize-none ${errors.errorCategoryId ? 'border-red-400' : ''}`}
                  />
                  {errors.errorCategoryId && <p className="text-red-500 text-xs mt-1">{errors.errorCategoryId}</p>}
                </div>
              )}
            </div>

            {/* Step 3: Contact Info */}
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold text-sm">3</div>
                <div>
                  <h2 className="font-semibold text-foreground">Ihre Kontaktdaten</h2>
                  <p className="text-xs text-muted-foreground">Damit wir Sie erreichen können</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Vorname *</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={e => handleChange('firstName', e.target.value)}
                    placeholder="Max"
                    className={`input-field ${errors.firstName ? 'border-red-400' : ''}`}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Nachname *</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={e => handleChange('lastName', e.target.value)}
                    placeholder="Mustermann"
                    className={`input-field ${errors.lastName ? 'border-red-400' : ''}`}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">E-Mail *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="max@beispiel.de"
                    className={`input-field ${errors.email ? 'border-red-400' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Telefon (optional)</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    placeholder="+49 123 456789"
                    className="input-field"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-1.5">Nachricht (optional)</label>
                <textarea
                  value={form.message}
                  onChange={e => handleChange('message', e.target.value)}
                  placeholder="Weitere Details zu Ihrem Problem..."
                  rows={4}
                  className="input-field resize-none"
                />
              </div>
            </div>

            <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-semibold">
              <Send size={18} />
              Anfrage per E-Mail senden
            </button>
            <p className="text-xs text-muted-foreground text-center -mt-4">
              Ihr E-Mail-Programm öffnet sich mit der vorausgefüllten Anfrage.
            </p>
          </form>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-5 sticky top-24">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Gamepad2 size={16} className="text-accent" />
                Ihre Auswahl
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Marke</span>
                  <span className="font-medium text-foreground">{selectedBrand?.name ?? '–'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Serie</span>
                  <span className="font-medium text-foreground">{selectedSeries?.name ?? '–'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Modell</span>
                  <span className="font-medium text-foreground">{selectedModel?.name ?? '–'}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="text-muted-foreground">Reparatur</span>
                  <span className="font-medium text-foreground text-right max-w-[140px]">
                    {selectedError?.name ?? (form.customRepairType || '–')}
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Mail size={13} className="mt-0.5 shrink-0 text-accent" />
                  <span>Nach dem Absenden erhalten Sie eine Bestätigung von uns.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-white mt-16 py-8">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Media Rabbit GmbH · <a href="mailto:info@mediarabbit.de" className="hover:text-foreground transition-colors">info@mediarabbit.de</a> · <a href="/impressum" className="hover:text-foreground transition-colors">Impressum</a></p>
        </div>
      </footer>
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-md shadow-sm">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-3">
            <Image
              src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
              alt="Media Rabbit GmbH Logo"
              width={120}
              height={40}
              className="h-9 w-auto object-contain"
              priority
            />
          </Link>
          <Link href="/" className="btn-ghost text-sm flex items-center gap-1.5">
            <ArrowLeft size={14} />
            Zurück
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function RepairRequestClient() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Laden...</div></div>}>
      <RepairRequestForm />
    </Suspense>
  );
}
